// useLinkedHighlightColors.js

import { ref, watch } from "vue";
import { useColorUtils } from "@/composables/useColorUtils";

/**
 * Liga a cor do texto e a cor do fundo de um destaque nos dois sentidos.
 *
 * Quem manda é a ÚLTIMA cor escolhida pelo usuário: escolher o texto recalcula
 * o fundo, escolher o fundo recalcula o texto, e a régua de temperatura
 * recalcula a outra a partir dela. A cor escolhida nunca é sobrescrita pela
 * outra - só o fundo escuro demais é clareado, para que o texto normal
 * continue legível sobre ele.
 *
 * A sincronização reage ao GESTO (os handlers abaixo, ligados ao @input dos
 * seletores), não à mudança de valor: com um watch em cada cor, uma
 * recalcularia a outra sem fim, e o carregamento dos dados salvos também
 * dispararia o recálculo.
 *
 * @param {object} options
 * @param {import("vue").WritableComputedRef<string>} options.textColor
 *   Cor do texto. O setter é do componente - é onde o sublinhado a acompanha.
 * @param {import("vue").WritableComputedRef<string>} options.backgroundColor
 * @param {import("vue").Ref<number>} options.temperature Régua 0–100.
 * @param {() => boolean} [options.isPaused] Verdadeiro enquanto os dados
 *   salvos são carregados: a régua muda junto e não deve recalcular nada.
 */
export function useLinkedHighlightColors({
  textColor,
  backgroundColor,
  temperature,
  isPaused = () => false,
}) {
  const { colorTextToBackground, colorBackgroundToText, legibleBackground } = useColorUtils();

  // Ao abrir, a referência é o texto, como era antes dos dois sentidos.
  const lastPicked = ref("text");

  const isHex = (color) => typeof color === "string" && /^#[0-9a-f]{6}$/i.test(color);

  function pickTextColor(color) {
    if (!isHex(color)) return;
    lastPicked.value = "text";
    textColor.value = color;
    backgroundColor.value = colorTextToBackground(color, temperature.value);
  }

  // Fundo escuro demais é clareado até o texto normal ficar legível sobre ele.
  function pickBackgroundColor(color) {
    if (!isHex(color)) return;
    lastPicked.value = "background";
    const background = legibleBackground(color);
    backgroundColor.value = background;
    textColor.value = colorBackgroundToText(background, temperature.value);
  }

  watch(temperature, (newTemp) => {
    if (isPaused()) return;

    if (lastPicked.value === "background") {
      if (isHex(backgroundColor.value))
        textColor.value = colorBackgroundToText(backgroundColor.value, newTemp);
    } else if (isHex(textColor.value)) {
      backgroundColor.value = colorTextToBackground(textColor.value, newTemp);
    }
  });

  return { pickTextColor, pickBackgroundColor };
}
